import { supabase } from './supabase';
import type { FileShareHistory } from '../types';

/**
 * Service for managing file share history
 */
export class FileHistoryService {
  /**
   * Add file share entry to history
   * Supports both authenticated and anonymous users
   */
  async addToHistory(entry: {
    user_id: string | null;
    filename: string;
    file_size: number;
    peer_id: string;
    peer_name?: string;
    transfer_type: 'sent' | 'received';
  }): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('file_share_history')
        .insert(entry);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Error adding to history:', error);
      return { error: error as Error };
    }
  }

  /**
   * Get file share history for a user with pagination
   */
  async getHistory(
    userId: string,
    options: {
      page?: number;
      limit?: number;
    } = {}
  ): Promise<{ data: FileShareHistory[]; error?: Error }> {
    try {
      const { page = 1, limit = 20 } = options;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      const { data, error } = await supabase
        .from('file_share_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      return { data: data as FileShareHistory[] };
    } catch (error) {
      console.error('Error fetching history:', error);
      return { data: [], error: error as Error };
    }
  }

  /**
   * Get total count of history entries for a user
   */
  async getHistoryCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('file_share_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching history count:', error);
      return 0;
    }
  }

  /**
   * Clear all history for a user (optional feature)
   */
  async clearHistory(userId: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('file_share_history')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Error clearing history:', error);
      return { error: error as Error };
    }
  }
}

export const fileHistoryService = new FileHistoryService();
